/**
 * useQueuedChat — buffer follow-up messages while a stream is in flight.
 *
 * - `enqueueOrSend(text, meta?)` sends immediately if idle, otherwise
 *   pushes to the tail of a queue. `meta` is an opaque sidecar
 *   (e.g., the active slash-command at submit time) that flows through
 *   to the `send` callback.
 * - When `isBusy` flips false (stream finished, errored, OR was
 *   canceled) and the queue is non-empty, the ENTIRE queue is joined
 *   with `\n\n` and fires as ONE merged user turn — the model sees
 *   them as a single coherent intent (Claude Code parity). The first
 *   item's `meta` rides the merged send (typically only the first
 *   message in a follow-up burst is a slash command).
 * - `popNewestQueued` removes the LAST item — what Esc unwinds when no
 *   stream is active. LIFO matches the "I just typed something I
 *   didn't mean to send" intuition.
 * - `clearQueue` drops all queued items in one go (✕ button on the chip).
 *
 * [FACTORY-293] The queue is mirrored to conversation-scoped `localStorage`
 * (via `lib/queue-persistence`) so a refresh/crash never discards typed-but-
 * unsent follow-ups. The merged-send path is UNCHANGED — persistence only
 * observes `queue` and clears when it empties (`setQueue([])` in the coalesce
 * effect fires the same persist effect), so coalesce parity is exact.
 *
 * Race-guard: `inFlightRef` is a synchronous boolean flipped inside the
 * same tick as the send call. Sibling calls in the same tick that see
 * `isBusy === false` (because React state hasn't propagated yet)
 * still observe `inFlightRef === true` and queue instead of
 * double-sending.
 *
 * Per apps/agent/plans/plan-queue-coalesce-claude-code-style.md.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { loadQueue, type QueuedChatMeta, type QueueEntry, saveQueue } from '../lib/queue-persistence'

export type { QueuedChatMeta }

interface UseQueuedChatOpts {
  /** Wraps useConversation.send. Hook owns its lifecycle. */
  send: (content: string, meta?: QueuedChatMeta) => Promise<string>
  /** Wraps useConversation.cancel. Used by Esc cascade. */
  cancel: () => Promise<void>
  /**
   * `true` whenever a turn is in flight — sending, awaiting, or
   * streaming. Broader than the legacy `isStreaming` gate so
   * messages typed during the sending/awaiting window (the gap
   * between Send-click and first stream event) buffer correctly
   * instead of firing a duplicate.
   */
  isBusy: boolean
  /**
   * [FACTORY-293] Active conversation id — the persistence key. Null before the
   * first send (nothing can be queued yet), when persistence is skipped.
   */
  conversationId: string | null
}

export interface UseQueuedChatResult {
  /** Queued message texts, in arrival order. UI uses this for the chip. */
  queue: string[]
  queuedMessage: string | null
  queueCount: number
  /** Send if idle, queue if busy. Trims; rejects empty. */
  enqueueOrSend: (text: string, meta?: QueuedChatMeta) => void
  /** Drop all queued items. */
  clearQueue: () => void
  /** Drop the most-recently queued item. Used by Esc cascade. */
  popNewestQueued: () => void
  /** Drop a specific queued item by index. Used by per-row ✕ buttons
   *  on the queue chip. No-op if index is out of range. */
  removeQueuedAt: (index: number) => void
}

/** Pure coalesce: join queued items into ONE merged turn (Claude Code parity).
 *  Exported for regression tests so restore is provably transparent to send. */
export function computeMergedSend(queue: QueueEntry[]): { merged: string; meta?: QueuedChatMeta } | null {
  if (queue.length === 0) {
    return null
  }
  const meta = queue[0]?.meta
  return { merged: queue.map((q) => q.content).join('\n\n'), ...(meta ? { meta } : {}) }
}

export function useQueuedChat({
  send,
  cancel: _cancel,
  isBusy,
  conversationId,
}: UseQueuedChatOpts): UseQueuedChatResult {
  void _cancel
  const [queue, setQueue] = useState<QueueEntry[]>([])
  const inFlightRef = useRef(false)

  // [FACTORY-293] Hydrate the queue from storage once per conversation id, then
  // mirror every change back. `hydratedConvIdRef` both guards the one-time load
  // and gates the persist effect so it never writes the prior conv's queue
  // under a new id. (ponytail: the transient write in the tick between a conv
  // switch and the hydrate re-render is overwritten in the next commit — only
  // observable on a browser-kill inside that microtask; accepted.)
  const hydratedConvIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (conversationId && hydratedConvIdRef.current !== conversationId) {
      hydratedConvIdRef.current = conversationId
      setQueue(loadQueue(conversationId))
    }
  }, [conversationId])
  useEffect(() => {
    if (!conversationId || hydratedConvIdRef.current !== conversationId) {
      return
    }
    saveQueue(conversationId, queue)
  }, [queue, conversationId])

  const enqueueOrSend = useCallback(
    (text: string, meta?: QueuedChatMeta) => {
      const t = text.trim()
      if (!t) {
        return
      }
      if (!isBusy && !inFlightRef.current) {
        inFlightRef.current = true
        void send(t, meta).finally(() => {
          inFlightRef.current = false
        })
      } else {
        setQueue((prev) => [...prev, { content: t, ...(meta ? { meta } : {}) }])
      }
    },
    [send, isBusy],
  )

  const popNewestQueued = useCallback(() => {
    setQueue((prev) => prev.slice(0, -1))
  }, [])

  const removeQueuedAt = useCallback((index: number) => {
    setQueue((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev
      }
      return prev.filter((_, i) => i !== index)
    })
  }, [])

  const clearQueue = useCallback(() => setQueue([]), [])

  // Auto-dequeue: when stream completes (or was canceled) and queue
  // non-empty, fire ALL queued items as ONE merged user turn so the
  // model sees them as a single coherent intent (Claude Code parity).
  // Meta from the first entry rides the merged send — slash commands
  // sit at the head of a burst in practice.
  useEffect(() => {
    if (isBusy) {
      return
    }
    if (inFlightRef.current) {
      return
    }
    const merged = computeMergedSend(queue)
    if (!merged) {
      return
    }
    inFlightRef.current = true
    setQueue([])
    void send(merged.merged, merged.meta).finally(() => {
      inFlightRef.current = false
    })
  }, [isBusy, queue, send])

  return {
    queue: queue.map((q) => q.content),
    queuedMessage: queue[0]?.content ?? null,
    queueCount: queue.length,
    enqueueOrSend,
    clearQueue,
    popNewestQueued,
    removeQueuedAt,
  }
}
